/**
 * American odds, and what a bet is actually worth.
 *
 * This is the one place in the betting feature that does arithmetic. The API
 * settles bets with it and the board quotes returns with it, so a disagreement
 * between "what the screen promised me" and "what the bookie paid" is not
 * expressible — which is the whole point, because that disagreement is the one
 * an argument on the 14th tee is made of.
 *
 * Two conventions are load-bearing:
 *
 * 1. EVERY amount is integer CENTS. Money in floats accumulates a fraction of a
 *    penny per operation and then the ledger stops adding up; the schema stores
 *    cents for the same reason.
 * 2. "Payout" ALWAYS includes the stake back. A bookie pays a winner their
 *    stake plus their profit, and returning profit alone from something named
 *    payout is exactly the kind of ambiguity that pays somebody $100 instead of
 *    $250. `profitCents` exists separately when the winnings alone are wanted.
 *
 * Nothing here throws. Bad input yields a conservative number rather than an
 * exception, because the caller on the server side is a request handler whose
 * 5xx responses get retried by the client forever — a market posted with junk
 * odds must fail validation loudly at the edge, not blow up mid-settlement.
 */

/** A market's lifecycle. Only an open market accepts bets. */
export type MarketStatus = 'proposed' | 'open' | 'closed' | 'settled'

/** How a market finished. `void` means it never really happened — refund. */
export type MarketResult = 'win' | 'lose' | 'void'

/**
 * A bet's lifecycle.
 *
 * `pending` is the important one: the wager exists on the board but the Venmo
 * has not landed, so it is not live and cannot win. Only the bookie moves it to
 * `confirmed`.
 */
export type BetStatus = 'pending' | 'confirmed' | 'won' | 'lost' | 'void'

/**
 * American odds have a hole in the middle: between -99 and +99 the notation is
 * meaningless (there is no such thing as "stake 50 to win 100" written as -50 —
 * that is +200). Rejecting the hole at the door stops a typo like `15` from
 * being silently read as a 6.67x payout.
 */
export const MIN_AMERICAN_ODDS = 100

/**
 * A ceiling on the line, not on ambition. At +100000 a $20 bet returns $20,020;
 * anything past that is a fat-fingered extra zero, and the twelve of them are
 * settling in Venmo.
 */
export const MAX_AMERICAN_ODDS = 100_000

export function isValidAmericanOdds(value: unknown): value is number {
  if (typeof value !== 'number' || !Number.isInteger(value)) return false
  const magnitude = Math.abs(value)
  return magnitude >= MIN_AMERICAN_ODDS && magnitude <= MAX_AMERICAN_ODDS
}

/**
 * A stake has to be a whole positive number of cents. Anything else is treated
 * as no stake at all, so a corrupt row settles to zero rather than to NaN —
 * a NaN in `payout_cents` poisons every total on the ledger screen, whereas a
 * zero is visibly wrong to exactly one person, who will say so.
 */
const cleanStake = (stakeCents: number): number =>
  Number.isFinite(stakeCents) && stakeCents > 0 ? Math.floor(stakeCents) : 0

/**
 * Winnings on a stake, excluding the stake itself.
 *
 * Negative odds quote what you must risk to win 100: at -150 you stake 150 to
 * win 100, so profit is stake x 100/150. Positive odds quote what 100 wins: at
 * +400 a 100 stake wins 400, so profit is stake x 400/100.
 *
 * Rounded to the nearest cent rather than floored. Flooring is the house's
 * habit and it invents a rake nobody in this group agreed to; these payouts are
 * checked against a phone calculator over dinner and are expected to match.
 */
export function profitCents(stakeCents: number, americanOdds: number): number {
  const stake = cleanStake(stakeCents)
  if (stake === 0 || !isValidAmericanOdds(americanOdds)) return 0
  const ratio =
    americanOdds > 0 ? americanOdds / 100 : 100 / Math.abs(americanOdds)
  return Math.round(stake * ratio)
}

/**
 * What lands back in the winner's pocket: stake plus winnings.
 *
 * Invalid odds return the bare stake, which is deliberately the same answer as
 * a void — if the line is unreadable the honest settlement is "nobody won
 * anything", not "nobody gets their money back".
 */
export function payoutCents(stakeCents: number, americanOdds: number): number {
  return cleanStake(stakeCents) + profitCents(stakeCents, americanOdds)
}

/**
 * The settlement rule, in one place.
 *
 * Note that a loss returns 0 rather than the negative of the stake: the stake
 * was already handed over in Venmo before the bet went live, so the payout
 * column is what moves BACK, and a loser is owed nothing. Modeling a loss as
 * -stake would double-count the money that already changed hands.
 */
export function settleCents(
  result: MarketResult,
  stakeCents: number,
  americanOdds: number,
): number {
  if (result === 'win') return payoutCents(stakeCents, americanOdds)
  if (result === 'void') return cleanStake(stakeCents)
  return 0
}

/**
 * The break-even win rate the line implies, 0..1 — the bookie's own estimate of
 * how likely the thing is, read straight off the price. Shown on the board so a
 * player can see that -150 is the book saying "60% of the time", which is a
 * more useful thing to argue with than the number itself.
 */
export function impliedProbability(americanOdds: number): number {
  if (!isValidAmericanOdds(americanOdds)) return 0
  const magnitude = Math.abs(americanOdds)
  return americanOdds > 0 ? 100 / (magnitude + 100) : magnitude / (magnitude + 100)
}

/** Lines always carry their sign — a bare `400` is not a price. */
export const formatAmerican = (americanOdds: number): string =>
  `${americanOdds > 0 ? '+' : ''}${americanOdds}`

/**
 * Cents as money, dropping a `.00` that carries no information. Whole dollars
 * are the overwhelmingly common case on a board where the chips are $5 to $100,
 * and "$25.00" in a dense row is two characters of noise.
 */
export function formatMoney(cents: number): string {
  const safe = Number.isFinite(cents) ? Math.round(cents) : 0
  const sign = safe < 0 ? '-' : ''
  const abs = Math.abs(safe)
  const dollars = Math.floor(abs / 100)
  const rest = abs % 100
  const whole = dollars.toLocaleString()
  return rest === 0
    ? `${sign}$${whole}`
    : `${sign}$${whole}.${rest.toString().padStart(2, '0')}`
}
